/**
 * Membership plan catalogue — the single source of truth for what we sell.
 *
 * Pure module: no Stripe, PayPal, DB or network. Safe on client and server.
 *
 * Today there is exactly one plan ($14.99 USD / month, recurring, cancel
 * anytime, no free trial). The shape is deliberately a list so additional
 * plans — annual, tiers, promotional offers — can be added later by appending
 * an entry, without rebuilding the membership page or the checkout flow.
 */

export type PlanInterval = "month" | "year";

export interface MembershipPlan {
  /** Stable internal id used in analytics and checkout payloads. */
  id: string;
  name: string;
  /** Short label, e.g. "Monthly", "6-Month Membership". */
  shortName: string;
  /** Short line under the plan name. */
  tagline: string;
  priceCents: number;
  currency: "usd";
  interval: PlanInterval;
  /** How many intervals per billing cycle (1 month, 6 months, 1 year). */
  intervalCount: number;
  /** Stripe Product this plan's price belongs to. */
  stripeProductId: string;
  /** Stripe recurring Price this plan checks out against. */
  stripePriceId: string;
  /**
   * Key in `paypal_config` holding the PayPal billing plan for this plan.
   * The PayPal plan id itself is admin-managed in the database.
   */
  paypalConfigKey: string;
  /** Optional badge, e.g. "Most popular" / "Best value". */
  badge?: string;
  /** e.g. "Billed monthly · Cancel anytime" */
  billingNote: string;
  /** e.g. "Equivalent to approximately $10/month" */
  equivalentNote?: string;
  /** Short at-a-glance monthly equivalent, e.g. "$10/month". */
  perMonthEquivalent: string;
  /** e.g. "Save approximately 33% compared with paying monthly." */
  savingNote?: string;
  ctaLabel: string;
  /**
   * 3–4 short, plan-specific lines used on the compact mobile pricing cards.
   * Deliberately not the full benefits list — that is shown once, below the
   * cards, so the cards stay scannable with one thumb.
   */
  highlights: readonly string[];
  /** True for the plan pre-selected on the membership page. */
  isDefault: boolean;
}

export const MEMBERSHIP_PLANS: readonly MembershipPlan[] = [
  {
    id: "membership_monthly",
    name: "Recovery Trauma™",
    shortName: "Monthly Membership",
    tagline: "Flexible Membership",
    priceCents: 1499,
    currency: "usd",
    interval: "month",
    intervalCount: 1,
    stripeProductId: "prod_V73720SAcsp9Yr",
    stripePriceId: "price_1U6p1jE9HWJ31kRoojxl0JPI",
    paypalConfigKey: "plan",
    billingNote: "Billed monthly · Cancel anytime",
    perMonthEquivalent: "$14.99/month",
    ctaLabel: "Choose Monthly",
    highlights: [
      "Full access to everything",
      "Cancel anytime",
      "No long-term commitment",
    ],
    isDefault: true,
  },
  {
    id: "membership_6month",
    name: "Recovery Trauma™ — 6 Months",
    shortName: "6-Month Membership",
    tagline: "Full access, billed twice a year",
    priceCents: 5999,
    currency: "usd",
    interval: "month",
    intervalCount: 6,
    stripeProductId: "prod_V7aOb9hrp6BnKD",
    stripePriceId: "price_1U7LE8E9HWJ31kRo1JnOe1j8",
    paypalConfigKey: "plan_6month",
    badge: "MOST POPULAR",
    billingNote: "Renews every 6 months unless cancelled.",
    equivalentNote: "Equivalent to approximately $10/month",
    perMonthEquivalent: "$10/month",
    savingNote: "Save approximately 33% compared with paying monthly.",
    ctaLabel: "Choose 6 Months",
    highlights: [
      "Full access to everything",
      "Save ~33% vs monthly",
      "Cancel anytime",
    ],
    isDefault: false,
  },
  {
    id: "membership_yearly",
    name: "Recovery Trauma™ — Yearly",
    shortName: "Yearly Membership",
    tagline: "Full access, billed once a year",
    priceCents: 8999,
    currency: "usd",
    interval: "year",
    intervalCount: 1,
    stripeProductId: "prod_V7aRkFoELvneSr",
    stripePriceId: "price_1U7LGIE9HWJ31kRosUlSBY39",
    paypalConfigKey: "plan_yearly",
    badge: "BEST VALUE",
    billingNote: "Renews annually unless cancelled.",
    equivalentNote: "Equivalent to approximately $7.50/month",
    perMonthEquivalent: "$7.50/month",
    savingNote: "Save approximately 50% compared with paying monthly.",
    ctaLabel: "Choose Yearly",
    highlights: [
      "Full access to everything",
      "Save ~50% vs monthly",
      "Cancel anytime",
    ],
    isDefault: false,
  },
] as const;

export const DEFAULT_PLAN: MembershipPlan =
  MEMBERSHIP_PLANS.find((p) => p.isDefault) ?? MEMBERSHIP_PLANS[0];

export function getPlan(id?: string | null): MembershipPlan {
  if (!id) return DEFAULT_PLAN;
  return MEMBERSHIP_PLANS.find((p) => p.id === id) ?? DEFAULT_PLAN;
}

/** True only for a plan id we actually sell today. */
export function isKnownPlanId(id?: string | null): boolean {
  if (!id) return false;
  return MEMBERSHIP_PLANS.some((p) => p.id === id);
}

/**
 * Server-side lookup. An empty id means "no explicit choice" and falls back to
 * the default plan, but an id we don't recognise is an error: silently billing
 * a different plan than the member picked is worse than failing loudly.
 */
export function getPlanStrict(id?: string | null): MembershipPlan {
  if (!id) return DEFAULT_PLAN;
  const plan = MEMBERSHIP_PLANS.find((p) => p.id === id);
  if (!plan) throw new Error(`Unknown membership plan: ${id}`);
  return plan;
}


export function getPlanByPriceId(priceId?: string | null): MembershipPlan | undefined {
  if (!priceId) return undefined;
  return MEMBERSHIP_PLANS.find((p) => p.stripePriceId === priceId);
}

/** "$14.99" */
export function formatPlanPrice(plan: MembershipPlan = DEFAULT_PLAN): string {
  const amount = plan.priceCents / 100;
  return `$${amount.toFixed(2)}`;
}

/** "every month" | "every 6 months" | "every year" */
export function formatPlanCycle(plan: MembershipPlan = DEFAULT_PLAN): string {
  if (plan.intervalCount === 1) return `every ${plan.interval}`;
  return `every ${plan.intervalCount} ${plan.interval}s`;
}

/** "$14.99 / month" | "$59.99 / 6 months" | "$89.99 / year" */
export function formatPlanPriceShort(plan: MembershipPlan = DEFAULT_PLAN): string {
  const unit =
    plan.intervalCount === 1 ? plan.interval : `${plan.intervalCount} ${plan.interval}s`;
  return `${formatPlanPrice(plan)} / ${unit}`;
}

/** "$14.99 USD/month" */
export function formatPlanPriceLong(plan: MembershipPlan = DEFAULT_PLAN): string {
  const unit =
    plan.intervalCount === 1 ? plan.interval : `${plan.intervalCount} ${plan.interval}s`;
  return `${formatPlanPrice(plan)} USD/${unit}`;
}


/** Convenience constants for copy that only ever refers to the standard plan. */
export const MEMBERSHIP_PRICE_CENTS = DEFAULT_PLAN.priceCents;
export const MEMBERSHIP_PRICE = formatPlanPrice(DEFAULT_PLAN);
export const MEMBERSHIP_PRICE_LONG = formatPlanPriceLong(DEFAULT_PLAN);

/**
 * What a member receives. Grouped so the membership page can present them in a
 * clear, scannable, mobile-friendly grid rather than one long list.
 */
export interface BenefitGroup {
  title: string;
  items: readonly string[];
}

export const MEMBERSHIP_BENEFIT_GROUPS: readonly BenefitGroup[] = [
  {
    title: "Calm your body",
    items: [
      "Guided recovery and grounding exercises",
      "Breathing exercises and calming tools",
      "Somatic and nervous-system practices",
      "Trauma-informed yoga",
    ],
  },
  {
    title: "Rest and settle",
    items: [
      "Guided meditations",
      "Calming sounds and soundscapes",
      "Sleep support and guided sleep sessions",
      "EFT tapping exercises",
      "Bilateral stimulation tools",
    ],
  },
  {
    title: "Process what you carry",
    items: [
      "Journaling exercises and guided prompts",
      "Shadow-work exercises",
      "Inner-child exercises",
      "Creative reflection activities",
      "Healing tools and worksheets",
    ],
  },
  {
    title: "Learn and belong",
    items: [
      "Trauma recovery resources and educational content",
      "The Recovery Trauma™ Library",
      "Courses and member content",
      "Guided 30-day healing journeys",
      "New tools, resources and content added to the platform",
    ],
  },
] as const;

export const MEMBERSHIP_BENEFITS: readonly string[] =
  MEMBERSHIP_BENEFIT_GROUPS.flatMap((g) => g.items);

/**
 * The member journey, shown as a progress indicator across the membership
 * page, account creation and payment selection.
 */
export const MEMBERSHIP_JOURNEY_STEPS = [
  "Choose membership",
  "Create account",
  "Complete payment",
  "Access membership",
] as const;

export type MembershipJourneyStep = 1 | 2 | 3 | 4;

/**
 * What every plan includes — identical full access on Monthly, 6 Months and
 * Yearly. Shown under the pricing cards on the plan-selection page.
 */
export const MEMBERSHIP_INCLUDED_FEATURES: readonly string[] = [
  "900+ Recovery Resources",
  "200+ Guided Exercises & Practices",
  "150+ Trauma Articles & Guides",
  "60+ DBT & CBT Skills",
  "50+ Calming Sounds & Soundscapes",
  "30+ Self-Assessments & Quizzes",
  "30+ Downloadable Workbooks & Guides",
  "Nervous System Regulation Exercises",
  "Grounding & Breathing Practices",
  "Somatic Practices",
  "EFT Tapping",
  "Guided Meditations",
  "Recovery Journal",
  "Dream Journal",
  "Shadow Work",
  "Inner Child Work",
  "Creative Reflection & Art Tools",
  "Guided 30-Day Healing Journeys",
  "Recovery Trauma™ Community",
  "New recovery resources and exercises added regularly",
] as const;

/**
 * Headline counts, shown first on the membership page so the scale of what is
 * included is clear without reading a twenty-line list.
 */
export const MEMBERSHIP_HEADLINE_STATS: readonly { value: string; label: string }[] = [
  { value: "900+", label: "Recovery Resources" },
  { value: "200+", label: "Guided Exercises & Practices" },
  { value: "150+", label: "Trauma Articles & Guides" },
  { value: "60+", label: "DBT & CBT Skills" },
  { value: "50+", label: "Calming Sounds & Soundscapes" },
  { value: "30+", label: "Self-Assessments & Quizzes" },
  { value: "30+", label: "Downloadable Workbooks & Guides" },
] as const;

/** The remaining features, grouped into three compact cards. */
export const MEMBERSHIP_FEATURE_GROUPS: readonly {
  title: string;
  items: readonly string[];
}[] = [
  {
    title: "Regulate",
    items: ["Grounding", "Breathing", "Somatic", "EFT", "Meditation"],
  },
  {
    title: "Reflect",
    items: [
      "Recovery Journal",
      "Dream Journal",
      "Shadow Work",
      "Inner Child",
      "Creative Reflection",
    ],
  },
  {
    title: "Heal & Grow",
    items: [
      "30-Day Healing Journeys",
      "Community",
      "New resources added regularly",
    ],
  },
] as const;

